import { ListChecks } from "@phosphor-icons/react";

export const NB_DISPOSITIONS = [
  "Fast-Track",
  "Stretch Assignment",
  "High Professional",
  "Retain & Reward",
  "Develop in Role",
  "Action Plan Required",
  "Manage Out",
];

export const TIERS = ["low", "med", "high"];
export const tierLabel = { low: "Low", med: "Medium", high: "High" };

// rows top->bottom = potential High, Med, Low ; cols left->right = performance Low, Med, High
const POT_ROWS = ["high", "med", "low"];
const PERF_COLS = ["low", "med", "high"];

const CELL = {
  "high-low": { label: "Potential Gem", tone: "amber" },
  "high-med": { label: "High Potential", tone: "emerald" },
  "high-high": { label: "Star", tone: "emerald" },
  "med-low": { label: "Inconsistent Player", tone: "amber" },
  "med-med": { label: "Core Player", tone: "blue" },
  "med-high": { label: "High Performer", tone: "emerald" },
  "low-low": { label: "Underperformer", tone: "red" },
  "low-med": { label: "Effective Specialist", tone: "blue" },
  "low-high": { label: "Trusted Professional", tone: "blue" },
};

const TONE_HEAD = {
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
  blue: "bg-blue-50 border-blue-200 text-blue-700",
  amber: "bg-amber-50 border-amber-200 text-amber-700",
  red: "bg-red-50 border-red-200 text-red-700",
};

export function NineBoxGrid({ people }) {
  const placed = (people || []).filter((p) => p.name?.trim());
  const at = (pot, perf) => placed.filter((p) => (p.potential || "med") === pot && (p.performance || "med") === perf);
  return (
    <div className="flex gap-3" data-testid="ninebox-grid">
      <div className="flex flex-col items-center justify-center">
        <span className="font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-emerald-700 [writing-mode:vertical-rl] rotate-180">Potential →</span>
      </div>
      <div className="flex-1">
        <div className="grid grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-sm overflow-hidden">
          {POT_ROWS.map((pot) =>
            PERF_COLS.map((perf) => {
              const key = `${pot}-${perf}`;
              const cell = CELL[key];
              const items = at(pot, perf);
              return (
                <div key={key} data-testid={`ninebox-cell-${key}`} className="bg-white min-h-[130px] flex flex-col">
                  <div className={`px-3 py-1.5 border-b ${TONE_HEAD[cell.tone]}`}>
                    <p className="font-mono text-[11px] font-bold uppercase tracking-wide leading-tight">{cell.label}</p>
                    <p className="text-[10px] opacity-70">{tierLabel[pot]} pot · {tierLabel[perf]} perf</p>
                  </div>
                  <div className="p-2.5 flex-1">
                    {items.length === 0 ? (
                      <span className="text-xs text-slate-300">—</span>
                    ) : (
                      <ul className="space-y-1">
                        {items.map((p) => (
                          <li key={p.id} className="text-xs text-slate-800 leading-snug">
                            <span className="font-medium">{p.name}</span>
                            {p.role ? <span className="text-slate-400"> · {p.role}</span> : null}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
        <p className="mt-2 text-center font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-emerald-700">Performance →</p>
      </div>
    </div>
  );
}

export default function NineBoxBlueprint({ doc }) {
  const people = (doc.payload?.people || []).filter((p) => p.name?.trim());
  return (
    <div data-testid="ninebox-blueprint">
      {doc.payload?.context && <p className="mb-6 text-sm text-slate-500 max-w-2xl">{doc.payload.context}</p>}
      <NineBoxGrid people={people} />

      <div className="mt-8">
        <div className="flex items-center gap-2 mb-3">
          <ListChecks size={18} weight="duotone" className="text-emerald-600" />
          <h2 className="font-mono text-sm font-bold uppercase tracking-[0.1em] text-slate-700">Talent Disposition Paths</h2>
        </div>
        {people.length === 0 ? (
          <p className="text-sm text-slate-400 border border-dashed border-slate-300 rounded-sm p-4 text-center">No people placed.</p>
        ) : (
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="ninebox-disposition-table">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3 font-semibold">Name</th>
                  <th className="px-5 py-3 font-semibold">Performance</th>
                  <th className="px-5 py-3 font-semibold">Potential</th>
                  <th className="px-5 py-3 font-semibold">Disposition Path</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {people.map((p) => (
                  <tr key={p.id}>
                    <td className="px-5 py-3.5 font-medium text-slate-900">{p.name}{p.role && <span className="block text-xs font-normal text-slate-400">{p.role}</span>}</td>
                    <td className="px-5 py-3.5 text-slate-700">{tierLabel[p.performance || "med"]}</td>
                    <td className="px-5 py-3.5 text-slate-700">{tierLabel[p.potential || "med"]}</td>
                    <td className="px-5 py-3.5 text-slate-700">{p.disposition || <span className="text-slate-300">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
